class ExoLoop extends HTMLElement {
  connectedCallback() {
    if (this.dataset.exoReady === 'true') return;
    this.dataset.exoReady = 'true';

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.tabs.length || !this.panels.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activate(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });
  }

  activate(index, focus = false) {
    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive && focus) tab.focus();
    });

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });
  }

  onKeydown(event, index) {
    let nextIndex = index;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % this.tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.activate(nextIndex, true);
  }
}

if (!customElements.get('exo-loop')) {
  customElements.define('exo-loop', ExoLoop);
}

const exoMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const exoRevealSelector = [
  '.exo-hero__content',
  '.exo-hero__intel',
  '.exo-hero__rail',
  '.exo-loop__lead',
  '.exo-loop__layout',
  '.exo-immersive__content',
  '.exo-section-head > *',
  '.exo-path',
  '.exo-device',
  '.exo-feature__visual',
  '.exo-feature__content',
  '.exo-metric',
  '.exo-proof-card',
  '.exo-membership__intro',
  '.exo-membership__compare',
  '.exo-quote',
  '.exo-final__inner',
  '.exo-contact__intro',
  '.exo-contact__panel',
  '.exo-contact__detail',
  '.exo-collection-hero__content',
  '.exo-collection-hero__meta',
  '.shopify-section--main-collection .collection__top-bar',
  '.shopify-section--main-collection .collection__facets',
  '.shopify-section--main-collection .collection__results'
].join(',');

let exoRevealObserver;

function getExoRevealObserver() {
  if (exoRevealObserver || !('IntersectionObserver' in window)) return exoRevealObserver;

  exoRevealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, {
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.12
  });

  return exoRevealObserver;
}

function initExoMotion(root = document) {
  const elements = Array.from(root.querySelectorAll(exoRevealSelector));

  elements.forEach((element, index) => {
    if (element.dataset.exoMotionReady === 'true') return;

    element.dataset.exoMotionReady = 'true';
    element.classList.add('exo-reveal');
    element.style.setProperty('--exo-reveal-delay', `${(index % 4) * 70}ms`);

    if (exoMotionQuery.matches || !('IntersectionObserver' in window)) {
      element.classList.add('is-visible');
      return;
    }

    getExoRevealObserver().observe(element);
  });
}

const exoDataNumberSelector = [
  '.exo-intel__number',
  '.exo-metric__value',
  '.exo-chart__summary strong',
  '.exo-feature__ui-row strong',
  '.exo-collection-hero__count',
  '.exo-membership__price strong',
  '.exo-loop__tab-number',
  '.exo-path__index > span:last-child',
  '.exo-contact__detail-index'
].join(',');
const exoDataVisualSelector = '.exo-intel__signals, .exo-feature__ui, .exo-chart';
let exoDataObserver;
let exoVisualObserver;

function getExoNumberParts(value) {
  const timeMatch = value.match(/^\s*(\d+):(\d+)\s*$/);

  if (timeMatch) {
    return {
      type: 'time',
      target: Number(timeMatch[1]) * 60 + Number(timeMatch[2]),
      original: value
    };
  }

  const numberMatch = value.match(/^(.*?)(-?\d+(?:\.\d+)?)(.*)$/);
  if (!numberMatch) return null;

  const decimals = numberMatch[2].includes('.') ? numberMatch[2].split('.')[1].length : 0;
  const integerPart = numberMatch[2].replace('-', '').split('.')[0];
  const padLength = decimals === 0 && integerPart.startsWith('0') ? integerPart.length : 0;

  return {
    type: 'number',
    prefix: numberMatch[1],
    target: Number(numberMatch[2]),
    suffix: numberMatch[3],
    decimals,
    padLength,
    original: value
  };
}

function formatExoNumber(parts, value) {
  if (parts.type === 'time') {
    const totalSeconds = Math.max(0, Math.round(value));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  let formattedValue = value.toFixed(parts.decimals);

  if (parts.padLength > 0) {
    formattedValue = String(Math.round(value)).padStart(parts.padLength, '0');
  }

  return `${parts.prefix}${formattedValue}${parts.suffix}`;
}

function animateExoNumber(element) {
  if (element.dataset.exoCountComplete === 'true') return;

  const original = element.dataset.exoCountValue || element.textContent.trim();
  const parts = getExoNumberParts(original);

  element.dataset.exoCountValue = original;
  element.dataset.exoCountComplete = 'true';

  if (!parts || exoMotionQuery.matches) {
    element.textContent = original;
    return;
  }

  element.classList.add('exo-counting');
  element.setAttribute('aria-label', original);

  const start = performance.now();
  const duration = 1550;

  function tick(now) {
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 4);
    element.textContent = formatExoNumber(parts, parts.target * eased);

    if (progress < 1) {
      window.requestAnimationFrame(tick);
    } else {
      element.textContent = original;
      element.classList.remove('exo-counting');
      element.classList.add('exo-count-complete');
    }
  }

  element.textContent = formatExoNumber(parts, 0);
  window.requestAnimationFrame(tick);
}

function getExoDataObserver() {
  if (exoDataObserver || !('IntersectionObserver' in window)) return exoDataObserver;

  exoDataObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      animateExoNumber(entry.target);
      observer.unobserve(entry.target);
    });
  }, {
    rootMargin: '0px 0px -6% 0px',
    threshold: 0.35
  });

  return exoDataObserver;
}

function getExoVisualObserver() {
  if (exoVisualObserver || !('IntersectionObserver' in window)) return exoVisualObserver;

  exoVisualObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-data-animated');
      observer.unobserve(entry.target);
    });
  }, {
    rootMargin: '0px 0px -6% 0px',
    threshold: 0.3
  });

  return exoVisualObserver;
}

function initExoDataMotion(root = document) {
  root.querySelectorAll(exoDataNumberSelector).forEach((element) => {
    if (element.dataset.exoCountReady === 'true') return;

    element.dataset.exoCountReady = 'true';
    element.classList.add('exo-data-number');

    if (exoMotionQuery.matches || !('IntersectionObserver' in window)) {
      animateExoNumber(element);
    } else {
      getExoDataObserver().observe(element);
    }
  });

  root.querySelectorAll(exoDataVisualSelector).forEach((element) => {
    if (element.dataset.exoDataReady === 'true') return;

    element.dataset.exoDataReady = 'true';
    element.classList.add('exo-data-ready');

    if (exoMotionQuery.matches || !('IntersectionObserver' in window)) {
      element.classList.add('is-data-animated');
    } else {
      getExoVisualObserver().observe(element);
    }
  });
}

let exoHeaderSection = document.querySelector('.shopify-section--header');
let exoHeaderFrame = 0;

function updateExoHeader() {
  exoHeaderFrame = 0;

  if (!exoHeaderSection || !exoHeaderSection.isConnected) {
    exoHeaderSection = document.querySelector('.shopify-section--header');
  }

  if (!exoHeaderSection) return;

  const scrollRange = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
  const progress = Math.min(Math.max(window.scrollY / scrollRange, 0), 1);

  exoHeaderSection.classList.toggle('exo-header-scrolled', window.scrollY > 24);
  exoHeaderSection.style.setProperty('--exo-header-progress', progress.toFixed(4));
}

function requestExoHeaderUpdate() {
  if (exoHeaderFrame) return;
  exoHeaderFrame = window.requestAnimationFrame(updateExoHeader);
}

window.addEventListener('scroll', requestExoHeaderUpdate, { passive: true });
window.addEventListener('resize', requestExoHeaderUpdate, { passive: true });
window.addEventListener('pageshow', requestExoHeaderUpdate);

document.documentElement.classList.add('exo-motion-ready');
initExoMotion();
initExoDataMotion();
updateExoHeader();

document.addEventListener('shopify:section:load', (event) => {
  initExoMotion(event.target);
  initExoDataMotion(event.target);

  if (event.target.matches?.('.shopify-section--header') || event.target.querySelector?.('.shopify-section--header')) {
    exoHeaderSection = document.querySelector('.shopify-section--header');
  }

  requestExoHeaderUpdate();
});

exoMotionQuery.addEventListener?.('change', (event) => {
  if (!event.matches) return;

  document.querySelectorAll('.exo-reveal').forEach((element) => {
    element.classList.add('is-visible');
    exoRevealObserver?.unobserve(element);
  });

  document.querySelectorAll(exoDataNumberSelector).forEach((element) => {
    if (element.dataset.exoCountValue) element.textContent = element.dataset.exoCountValue;
    element.classList.remove('exo-counting');
    exoDataObserver?.unobserve(element);
  });

  document.querySelectorAll(exoDataVisualSelector).forEach((element) => {
    element.classList.add('is-data-animated');
    exoVisualObserver?.unobserve(element);
  });
});
